import type { WorkflowGraph, WorkflowGraphEdge, WorkflowGraphNode } from '../../api/workflowRuns'
import { isGarbledWorkflowText, resolveWorkflowNodeLabel } from './workflowDefinitionDisplay'

export type WorkflowControlGraphSlice = Pick<WorkflowGraph, 'nodes' | 'edges'>
export type WorkflowControlInputSource = {
  upstreamNode: WorkflowGraphNode
  fieldKey: string
  /** 给用户看的摘要，不含技术字段名 */
  summaryLabel: string
}

const DATA_FIELD_LABELS: Record<string, string> = {
  input: '运行输入',
  prompt: '文本结果',
  images: '图片结果',
  characterImages: '人物图片',
  sceneImages: '场景图片',
  llmOutput: '模型输出',
  storyboard: '分镜结果',
  storyboardText: '分镜文本',
  story: '剧情文本',
  result: '运行结果',
}

function nodeDisplayName(node: WorkflowGraphNode): string {
  return resolveWorkflowNodeLabel(node, 'custom')
}

function normalizeTargetPort(edge: WorkflowGraphEdge): string {
  const port = String(edge.targetPort ?? '').trim()
  return port || 'main'
}

function collectLoopBodyNodeIds(loopNodeId: string, edges: WorkflowGraphEdge[]): Set<string> {
  const nodes = new Set<string>()
  const queue: string[] = []
  for (const edge of edges) {
    if (edge.sourceNodeId !== loopNodeId) continue
    const sourcePort = String(edge.sourcePort ?? '').trim() || String(edge.route ?? '').trim() || 'out'
    if (sourcePort !== 'body') continue
    const target = String(edge.targetNodeId ?? '').trim()
    if (target && target !== loopNodeId) queue.push(target)
  }
  while (queue.length > 0) {
    const current = queue.shift()!
    if (!nodes.add(current)) continue
    for (const edge of edges) {
      if (edge.sourceNodeId !== current) continue
      const target = String(edge.targetNodeId ?? '').trim()
      if (!target || target === loopNodeId) continue
      queue.push(target)
    }
  }
  return nodes
}

/** 与后端各 StepHandler 写入运行数据的字段名对齐 */
export function resolveWorkflowNodeStateField(node: WorkflowGraphNode): string | null {
  const config = (node.config ?? {}) as Record<string, unknown>
  switch (node.type) {
    case 'start.input': {
      const field = String(config.inputField ?? 'input').trim()
      return field || 'input'
    }
    case 'llm.prompt': {
      const field = String(config.outputField ?? 'prompt').trim()
      return field || 'prompt'
    }
    case 'image.generate': {
      const field = String(config.outputField ?? 'images').trim()
      return field || 'images'
    }
    case 'text.rewrite': {
      const field = String(config.outputField ?? 'prompt').trim()
      return field || 'prompt'
    }
    case 'storyboard.draft':
      return String(config.outputField ?? 'storyboard').trim() || 'storyboard'
    case 'comic.project.create':
      return String(config.outputField ?? 'linkedComicProject').trim() || 'linkedComicProject'
    case 'comic.episode.generate':
      return String(config.outputField ?? 'generatedEpisode').trim() || 'generatedEpisode'
    default:
      return null
  }
}

export function workflowControlSourceDataLabel(fieldKey: string): string {
  const key = fieldKey.trim()
  return DATA_FIELD_LABELS[key] ?? key
}

function mainInputEdges(graph: WorkflowControlGraphSlice, nodeId: string): WorkflowGraphEdge[] {
  return graph.edges.filter(
    (edge) => edge.targetNodeId === nodeId && normalizeTargetPort(edge) === 'main',
  )
}

/** 消费主输入的节点：配置里用哪个 key 存上游产出字段名 */
export const WORKFLOW_MAIN_INPUT_BINDING: Record<string, string> = {
  'image.generate': 'promptField',
  'text.rewrite': 'inputField',
  'storyboard.draft': 'inputField',
}

export function workflowMainInputBindingFieldKey(nodeType: string): string | null {
  return WORKFLOW_MAIN_INPUT_BINDING[nodeType] ?? null
}

/**
 * 从「主输入」连线的唯一步骤推导可读数据来源（不含循环体回连等特殊逻辑）。
 */
export function resolveWorkflowMainInputSource(
  graph: WorkflowControlGraphSlice,
  node: WorkflowGraphNode,
): WorkflowControlInputSource | null {
  const nodeById = new Map(graph.nodes.map((item) => [item.nodeId, item] as const))
  const incoming = mainInputEdges(graph, node.nodeId)
  if (incoming.length !== 1) return null

  const upstream = nodeById.get(incoming[0].sourceNodeId)
  if (!upstream) return null
  const fieldKey = resolveWorkflowNodeStateField(upstream)
  if (!fieldKey) return null

  const stepName = !isGarbledWorkflowText(upstream.name) ? upstream.name.trim() : nodeDisplayName(upstream)
  return {
    upstreamNode: upstream,
    fieldKey,
    summaryLabel: `使用上一步「${stepName}」的${workflowControlSourceDataLabel(fieldKey)}`,
  }
}

export function workflowMainInputSourceIssueMessage(
  graph: WorkflowControlGraphSlice,
  node: WorkflowGraphNode,
): string | null {
  const nodeName = nodeDisplayName(node)
  const incoming = mainInputEdges(graph, node.nodeId)
  if (incoming.length === 0) {
    return `「${nodeName}」请将上一步接到本节点的「主输入」`
  }
  if (incoming.length > 1) {
    return `「${nodeName}」主输入只能连接一个上一步`
  }
  const resolved = resolveWorkflowMainInputSource(graph, node)
  if (!resolved) {
    return `「${nodeName}」上一步暂无可读取的产出，请连接开始、LLM 或生图等步骤`
  }
  return null
}

/**
 * 控制节点只处理「主输入」连入的上一步结果；sourceField 由连线自动推导，用户不选。
 * 循环节点：若循环体已回连，以回连步骤的产出作为每轮退出判断依据。
 */
export function resolveWorkflowControlInputSource(
  graph: WorkflowControlGraphSlice,
  node: WorkflowGraphNode,
): WorkflowControlInputSource | null {
  const nodeById = new Map(graph.nodes.map((item) => [item.nodeId, item] as const))
  const incoming = mainInputEdges(graph, node.nodeId)
  if (incoming.length === 0) return null

  let chosen: WorkflowGraphEdge | null = null

  if (node.type === 'loop.until') {
    const bodyNodes = collectLoopBodyNodeIds(node.nodeId, graph.edges)
    const bodyReturn = incoming.filter((edge) => bodyNodes.has(edge.sourceNodeId))
    if (bodyReturn.length === 1) {
      chosen = bodyReturn[0]
    } else if (bodyReturn.length === 0 && incoming.length === 1) {
      chosen = incoming[0]
    } else {
      return null
    }
  } else {
    return resolveWorkflowMainInputSource(graph, node)
  }

  const upstream = nodeById.get(chosen!.sourceNodeId)
  if (!upstream) return null
  const fieldKey = resolveWorkflowNodeStateField(upstream)
  if (!fieldKey) return null

  const stepName = !isGarbledWorkflowText(upstream.name) ? upstream.name.trim() : nodeDisplayName(upstream)
  return {
    upstreamNode: upstream,
    fieldKey,
    summaryLabel: `依据上一步「${stepName}」的${workflowControlSourceDataLabel(fieldKey)}`,
  }
}

export function workflowControlInputSourceIssueMessage(
  graph: WorkflowControlGraphSlice,
  node: WorkflowGraphNode,
): string | null {
  const nodeName = nodeDisplayName(node)
  const incoming = mainInputEdges(graph, node.nodeId)
  if (incoming.length === 0) {
    return `「${nodeName}」请将上一步接到本节点的「主输入」`
  }
  if (node.type !== 'loop.until' && incoming.length > 1) {
    return `「${nodeName}」主输入只能连接一个上一步`
  }
  const bodyNodes = node.type === 'loop.until' ? collectLoopBodyNodeIds(node.nodeId, graph.edges) : new Set<string>()
  const bodyReturn = incoming.filter((edge) => bodyNodes.has(edge.sourceNodeId))
  if (node.type === 'loop.until' && bodyReturn.length > 1) {
    return `「${nodeName}」循环体只能有一条回连线`
  }
  if (node.type === 'loop.until' && bodyReturn.length === 0 && incoming.length > 1) {
    return `「${nodeName}」进入循环前只能有一个上一步`
  }
  const resolved = resolveWorkflowControlInputSource(graph, node)
  if (!resolved) {
    return `「${nodeName}」上一步暂无可判断的产出，请连接开始、LLM 或生图等步骤`
  }
  return null
}
